import React,{useState} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import {assessmentQuestions} from '../data/questions.js';
import SafeIcon from '../common/SafeIcon.jsx';
import * as FiIcons from 'react-icons/fi';
import Header from './Header.jsx';
import QuestionTooltip from './QuestionTooltip.jsx';

const {FiChevronLeft,FiChevronRight,FiCheck}=FiIcons;

const Assessment=({onComplete,onBackToHome,onCancel})=> {
  const [currentQuestionIndex,setCurrentQuestionIndex]=useState(0);
  const [responses,setResponses]=useState({});
  const [progress,setProgress]=useState(0);

  const currentQuestion=assessmentQuestions[currentQuestionIndex];
  const totalQuestions=assessmentQuestions.length;

  const handleResponse=(questionId,value)=> {
    const newResponses={...responses,[questionId]: value};
    setResponses(newResponses);
    
    // Auto-advance for single-select questions
    if (currentQuestion.type==='select') {
      setTimeout(()=> {
        if (currentQuestionIndex < totalQuestions - 1) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
          setProgress(((currentQuestionIndex + 1) / totalQuestions) * 100);
        } else {
          // Complete assessment if this is the last question
          onComplete(newResponses);
        }
      },600);
    }
  };

  const handleMultiSelect=(questionId,option)=> {
    const currentValues=responses[questionId] || [];
    let newValues;
    let shouldAutoAdvance=false;

    // List of exclusive options that should clear others and auto-advance
    if (option==='none' || option==='live-alone') {
      // If the exclusive option is already selected,deselect it (toggle off)
      if (currentValues.includes(option)) {
        newValues=[];
      } else {
        // If it's not selected,select it exclusively (clear others)
        newValues=[option];
        shouldAutoAdvance=true;
      }
    } else {
      // If selecting a normal option,remove any exclusive options first
      const filteredValues=currentValues.filter(v=> v !=='none' && v !=='live-alone');
      if (filteredValues.includes(option)) {
        // Deselect if already present
        newValues=filteredValues.filter(v=> v !==option);
      } else {
        // Add if not present
        newValues=[...filteredValues,option];
      }
    }

    const newResponses={...responses,[questionId]: newValues};
    setResponses(newResponses);

    // Auto-advance if an exclusive option was selected (improving UX)
    if (shouldAutoAdvance) {
      setTimeout(()=> {
        if (currentQuestionIndex < totalQuestions - 1) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
          setProgress(((currentQuestionIndex + 1) / totalQuestions) * 100);
        } else {
          onComplete(newResponses);
        }
      },600);
    }
  };

  const goToNext=()=> {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setProgress(((currentQuestionIndex + 1) / totalQuestions) * 100);
    } else {
      // Complete assessment
      onComplete(responses);
    }
  };

  const goToPrevious=()=> {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setProgress((currentQuestionIndex / totalQuestions) * 100);
    }
  };

  const isAnswered=()=> {
    const answer=responses[currentQuestion.id];
    // Check if answer exists and isn't empty array
    return answer !==undefined && answer !==null && (Array.isArray(answer) ? answer.length > 0 : true);
  };

  const getCategoryColor=(category)=> {
    const colors={
      demographics: 'bg-[#F2674C]',
      living: 'bg-[#f9ce6f]',
      lifestyle: 'bg-[#a2b8cd]',
      personality: 'bg-[#bcb64b]',
      geographic: 'bg-[#282f3a]',
      care: 'bg-[#a2b8cd]',
      preferences: 'bg-[#F2674C]'
    };
    return colors[category] || 'bg-gray-500';
  };

  const getCategoryName=(category)=> {
    const names={
      demographics: 'Personal Info',
      living: 'Living Situation',
      lifestyle: 'Lifestyle',
      personality: 'Personality',
      geographic: 'Location',
      care: 'Care Abilities',
      preferences: 'Preferences'
    };
    return names[category] || category;
  };

  const handleCancelAssessment=()=> {
    if (window.confirm('Are you sure you want to cancel the assessment? Your progress will be lost.')) {
      onCancel();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f3ede6] to-white flex flex-col">
      <Header onBackToHome={onBackToHome} showLogo={true} showHomeButton={false} showCancelButton={true} onCancel={handleCancelAssessment} />
      
      <div className="flex-grow flex flex-col items-center justify-center p-4 md:p-6 w-full max-w-7xl mx-auto">
        
        {/* Progress & Category - Compacted */}
        <div className="w-full max-w-2xl mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className={`inline-flex items-center px-3 py-1 rounded-xl text-xs font-bold text-white ${getCategoryColor(currentQuestion.category)}`}>
               {getCategoryName(currentQuestion.category)}
            </span>
            <span className="text-xs font-semibold text-gray-500">
              {currentQuestionIndex + 1} / {totalQuestions}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <motion.div 
              className="bg-[#F2674C] h-1.5 rounded-full" 
              initial={{width: 0}} 
              animate={{width: `${progress}%`}} 
              transition={{duration: 0.5,ease: [0.25,0.25,0,1]}} 
            />
          </div>
        </div>

        {/* Main Interaction Area: Left Arrow - Card - Right Arrow */}
        <div className="flex items-center justify-center w-full gap-4 md:gap-8 max-w-5xl">
            
            {/* Previous Button */}
            <button 
                onClick={goToPrevious} 
                disabled={currentQuestionIndex === 0}
                className={`flex-shrink-0 w-12 h-12 md:w-16 md:h-16 rounded-full bg-white shadow-lg border border-gray-100 text-[#282f3a] transition-all duration-300 hover:scale-110 hover:bg-gray-50 disabled:opacity-0 disabled:cursor-default flex items-center justify-center z-10 ${currentQuestionIndex === 0 ? 'invisible' : ''}`}
                aria-label="Previous question"
            >
                <SafeIcon icon={FiChevronLeft} className="w-6 h-6 md:w-8 md:h-8" />
            </button>

            {/* Question Card */}
            <div className="flex-1 w-full max-w-2xl">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentQuestionIndex}
                        initial={{opacity: 0,x: 40}}
                        animate={{opacity: 1,x: 0}}
                        exit={{opacity: 0,x: -40}}
                        transition={{duration: 0.3,ease: [0.25,0.25,0,1]}}
                        className="w-full"
                    >
                         <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6 md:p-10">
                            <div className="flex items-center justify-center mb-6 md:mb-10">
                                <h2 className="text-xl md:text-3xl font-bold text-[#282f3a] text-center leading-tight">
                                    {currentQuestion.question}
                                </h2>
                                <QuestionTooltip questionId={currentQuestion.id} />
                            </div>

                            {/* Options Container - Scrollable if needed with custom scrollbar */}
                            <div className="max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                                {/* Single Select Options */}
                                {currentQuestion.type==='select' && (
                                    <div className="space-y-3">
                                    {currentQuestion.options.map((option,index)=> (
                                        <motion.button
                                            key={option.value}
                                            initial={{opacity: 0,y: 10}}
                                            animate={{opacity: 1,y: 0}}
                                            transition={{delay: index * 0.03,duration: 0.2}}
                                            onClick={()=> handleResponse(currentQuestion.id,option.value)}
                                            className={`w-full p-4 md:p-5 text-left rounded-2xl border-2 transition-all duration-200 ${
                                                responses[currentQuestion.id]===option.value 
                                                ? 'border-[#F2674C] bg-[#F2674C]/10 text-[#F2674C] shadow-md' 
                                                : 'border-gray-100 hover:border-gray-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="font-semibold text-base md:text-lg">{option.label}</span>
                                                {responses[currentQuestion.id]===option.value && (
                                                    <div className="w-6 h-6 bg-[#F2674C] rounded-full flex items-center justify-center flex-shrink-0">
                                                        <SafeIcon icon={FiCheck} className="text-white w-3 h-3" />
                                                    </div>
                                                )}
                                            </div>
                                        </motion.button>
                                    ))}
                                    </div>
                                )}

                                {/* Multi Select Options */}
                                {currentQuestion.type==='multiselect' && (
                                    <div className="space-y-3">
                                    {currentQuestion.options.map((option,index)=> {
                                        const isSelected=(responses[currentQuestion.id] || []).includes(option.value);
                                        return (
                                        <motion.button
                                            key={option.value}
                                            initial={{opacity: 0,y: 10}}
                                            animate={{opacity: 1,y: 0}}
                                            transition={{delay: index * 0.03,duration: 0.2}}
                                            onClick={()=> handleMultiSelect(currentQuestion.id,option.value)}
                                            className={`w-full p-4 md:p-5 text-left rounded-2xl border-2 transition-all duration-200 ${
                                                isSelected 
                                                ? 'border-[#F2674C] bg-[#F2674C]/10 text-[#F2674C] shadow-md' 
                                                : 'border-gray-100 hover:border-gray-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="font-semibold text-base md:text-lg">{option.label}</span>
                                                {isSelected && (
                                                    <div className="w-6 h-6 bg-[#F2674C] rounded-full flex items-center justify-center flex-shrink-0">
                                                        <SafeIcon icon={FiCheck} className="text-white w-3 h-3" />
                                                    </div>
                                                )}
                                            </div>
                                        </motion.button>
                                        );
                                    })}
                                    <p className="text-xs text-gray-400 mt-4 text-center font-medium uppercase tracking-wider">
                                        Select all that apply
                                    </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Next Button */}
            <button 
                onClick={goToNext} 
                disabled={!isAnswered()}
                className={`flex-shrink-0 w-12 h-12 md:w-16 md:h-16 rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F2674C] flex items-center justify-center z-10 ${
                    !isAnswered()
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-[#F2674C] text-white hover:bg-[#e85a3e] shadow-[#F2674C]/25'
                }`}
                aria-label={currentQuestionIndex === totalQuestions - 1 ? "Get Results" : "Next question"}
            >
                <SafeIcon 
                    icon={currentQuestionIndex === totalQuestions - 1 ? FiCheck : FiChevronRight} 
                    className="w-6 h-6 md:w-8 md:h-8" 
                />
            </button>
        </div>

      </div>
    </div>
  );
};

export default Assessment;